'use strict';

/**
 * Which pages each role belongs on. Convenience routing only - the APIs enforce the
 * same rules from the role on the session document.
 * Dependency free CommonJS so the server, the bundles and mocha can all load it.
 */

const roles = {
    SUPER_ADMIN: "super_admin",
    ADMIN: "admin",
    JUDGE: "judge",
    USER: "user",
};

// The pages a session can be sent to, relative to the school
const pages = {
    SUBMISSION: "submit",
    CASE_LOOKUP: "selectCase",
    ADMIN: "admin",
    APPEALS_ADMIN: "appealsAdmin",
};

// The cookie the server writes the role hint into. Declared here because both sides of
// the wire import this module - two literals would let a rename break the client silently.
const ROLE_COOKIE = "authRole";

// Why a session was sent away, for the destination page to explain
const denialReasons = {
    NEEDS_ADMIN: "needs_admin",
    NEEDS_LOGIN: "needs_login",
};

// Keyed by denialReasons, plus a fallback so no one is sent away unexplained
const denialMessages = {
    needs_admin: "אין לך הרשאה לצפות בממשק הניהול.",
    needs_login: "יש להתחבר לפני הכניסה לממשק הניהול.",
    default: "אין לך הרשאה לצפות בדף המבוקש.",
};

function pathTo(schoolName, page) {
    return "/" + schoolName + "/" + page;
}

// May manage the school: members, settings, appeals, the case table
function mayUseAdminInterface(role) {
    return role === roles.ADMIN || role === roles.SUPER_ADMIN;
}

// May open a single case to judge it
function mayViewCase(role) {
    return mayUseAdminInterface(role) || role === roles.JUDGE;
}

/**
 * The page a role belongs on. Without a session that's the submission form, which is
 *  also where the password prompt and the admin login live.
 */
function homePage(role) {
    if (mayUseAdminInterface(role))
        return pages.ADMIN;
    if (role === roles.JUDGE)
        return pages.CASE_LOOKUP;
    return pages.SUBMISSION;
}

function homePathFor(role, schoolName) {
    return pathTo(schoolName, homePage(role));
}

/**
 * @param isCaseView Whether the URL points at a single case, which is all a judge gets
 * @returns {Object|undefined} {path, reason} to redirect, or undefined to stay put
 */
function adminPageRedirect(role, schoolName, isCaseView) {
    if (mayUseAdminInterface(role))
        return undefined;
    // The one admin page a judge is meant to use
    if (role === roles.JUDGE && isCaseView)
        return undefined;

    return {
        path: homePathFor(role, schoolName),
        reason: role ? denialReasons.NEEDS_ADMIN : denialReasons.NEEDS_LOGIN
    };
}

/**
 * Appeals are the appeal committee's business, so judges get no more access than users.
 * @returns {Object|undefined} {path, reason} to redirect, or undefined to stay put
 */
function appealsAdminPageRedirect(role, schoolName) {
    // Same set as the admin interface: appeals are not a judge's business
    if (mayUseAdminInterface(role))
        return undefined;

    return {
        path: homePathFor(role, schoolName),
        reason: role ? denialReasons.NEEDS_ADMIN : denialReasons.NEEDS_LOGIN
    };
}

module.exports = {
    roles: roles,
    pages: pages,
    ROLE_COOKIE: ROLE_COOKIE,
    denialReasons: denialReasons,
    denialMessages: denialMessages,
    pathTo: pathTo,
    mayUseAdminInterface: mayUseAdminInterface,
    mayViewCase: mayViewCase,
    homePage: homePage,
    homePathFor: homePathFor,
    adminPageRedirect: adminPageRedirect,
    appealsAdminPageRedirect: appealsAdminPageRedirect,
};
